<template>
  <b-modal
    v-model="show"
    size="xl"
    hide-footer
    no-enforce-focus
    dialog-class="image-viewer-modal"
    title="Visualizador de imagens"
    @shown="onShown"
    @hidden="onClose"
  >
    <div v-if="currentImage" class="image-viewer-content">
      <div class="main-image-section">
        <img :src="currentImageUrl" class="main-image" />
        <div class="image-caption mt-3 text-center">
          {{ currentImageName }}
        </div>
      </div>
      <div class="image-thumbnails-container" ref="thumbnailsContainer">
        <img
          v-for="(img, idx) in imageFiles"
          :key="img.id"
          :data-idx="idx"
          :src="thumbnailSrc(img, idx)"
          :alt="img.name"
          ref="thumb"
          class="thumbnail-mini"
          :class="{ active: idx === currentImageIdx }"
          @click="goToImage(idx)"
        />
      </div>
      <button
        v-if="showSelectButton"
        class="btn btn-primary mt-3"
        @click="selectImage(currentImage)"
       >
        Selecionar
      </button>
    </div>
    <div v-else class="image-viewer-empty">
      <div class="empty-state">
        <div class="empty-title">Nenhuma imagem na galeria</div>
        <div class="empty-subtitle">Adicione imagens para visualizar e selecionar.</div>
      </div>
    </div>
  </b-modal>
</template>

<script>

export default {
  name: "AttachmentViewer",
  props: {
    attachments: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      type: Boolean,
      default: false
    },
    startIndex: {
      type: Number,
      default: null
    },
    showSelectButton: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      show: this.value,
      currentImageIdx: this.startIndex ?? 0,
      folder: {},
      renderFolders: true,
      loadedThumbs: [],
      thumbObserver: null,
      placeholderSrc: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==",
      boundKeydown: null,
    };
  },
  watch: {
    value(val) {
      this.show = val;
    },
    show(val) {
      this.$emit("input", val);
      if (!val) {
        this.teardownThumbObserver();
      }
    },
    imageFiles() {
      if (!this.show) return;
      this.$nextTick(() => {
        this.resetThumbLoading();
        this.initThumbObserver();
      });
    }
  },
  computed: {
    imageFiles() {
      return this.attachments.filter(f => f.mime_type?.startsWith("image/"));
    },
    currentImage() {
      return this.imageFiles[this.currentImageIdx] || null;
    },
    currentImageUrl() {
      return this.currentImage?.url || null;
    },
    currentImageName() {
      return this.currentImage?.name || "";
    }
  },
  methods: {
    selectImage(image) {
      const payload = { image, index: this.currentImageIdx };
      this.$emit('select', payload);
      this.show = false;
    },
    onShown() {
      // Resincroniza sempre que o modal abre, mesmo se startIndex tiver o mesmo
      // valor da abertura anterior (clicar na mesma foto duas vezes não dispara
      // watch de prop, já que o valor não muda) e mesmo se o usuário tiver
      // navegado pra outra foto dentro do modal antes de fechar.
      this.currentImageIdx = this.startIndex ?? 0;
      this.$nextTick(() => {
        this.resetThumbLoading();
        this.initThumbObserver();
        this.scrollToCurrentThumb();
      });
      if (!this.boundKeydown) {
        this.boundKeydown = (e) => this.handleKeydown(e);
        window.addEventListener("keydown", this.boundKeydown, true);
      }
    },
    onClose() {
      this.teardownThumbObserver();
      if (this.boundKeydown) {
        window.removeEventListener("keydown", this.boundKeydown, true);
        this.boundKeydown = null;
      }
    },
    goToImage(idx) {
      if (idx < 0 || idx >= this.imageFiles.length) return;
      this.currentImageIdx = idx;
      this.ensureThumbLoaded(idx);
    },
    thumbnailSrc(img, idx) {
      return this.loadedThumbs[idx] ? img.url : this.placeholderSrc;
    },
    ensureThumbLoaded(idx) {
      if (idx < 0 || idx >= this.loadedThumbs.length) return;
      if (this.loadedThumbs[idx]) return;
      this.$set(this.loadedThumbs, idx, true);
    },
    resetThumbLoading() {
      this.loadedThumbs = this.imageFiles.map(() => false);
      this.ensureThumbLoaded(this.currentImageIdx);
      this.loadInitiallyVisibleThumbs();
    },
    loadInitiallyVisibleThumbs() {
      const container = this.$refs.thumbnailsContainer;
      const thumbs = this.$refs.thumb || [];
      if (!container || !thumbs.length) return;
      const containerRect = container.getBoundingClientRect();
      thumbs.forEach((el, idx) => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.right > containerRect.left && rect.left < containerRect.right;
        if (isVisible) this.$set(this.loadedThumbs, idx, true);
      });
    },
    initThumbObserver() {
      this.teardownThumbObserver();
      const container = this.$refs.thumbnailsContainer;
      const thumbs = this.$refs.thumb || [];
      if (!container || !thumbs.length) return;
      if (!("IntersectionObserver" in window)) {
        this.loadedThumbs = this.imageFiles.map(() => true);
        return;
      }
      this.thumbObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const idx = Number(entry.target.dataset.idx);
            if (!Number.isNaN(idx)) this.$set(this.loadedThumbs, idx, true);
            if (this.thumbObserver) this.thumbObserver.unobserve(entry.target);
          });
        },
        { root: container, rootMargin: "0px", threshold: 0.1 }
      );
      thumbs.forEach(el => this.thumbObserver.observe(el));
    },
    teardownThumbObserver() {
      if (!this.thumbObserver) return;
      this.thumbObserver.disconnect();
      this.thumbObserver = null;
    },
    handleKeydown(e) {
      if (!this.show) return;
      if (e.key === "ArrowRight") this.nextImage();
      if (e.key === "ArrowLeft") this.prevImage();
      if (e.key === "Escape") this.show = false;
    },
    nextImage() {
      if (!this.imageFiles.length) return;
      this.currentImageIdx = (this.currentImageIdx + 1) % this.imageFiles.length;
      this.scrollToCurrentThumb();
    },
    prevImage() {
      if (!this.imageFiles.length) return;
      this.currentImageIdx = (this.currentImageIdx - 1 + this.imageFiles.length) % this.imageFiles.length;
      this.scrollToCurrentThumb();
    },
    scrollToCurrentThumb() {
      this.$nextTick(() => {
        const container = this.$refs.thumbnailsContainer;
        const thumbs = this.$refs.thumb;
        const el = Array.isArray(thumbs) ? thumbs[this.currentImageIdx] : null;
        if (!container || !el) return;
        const target = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
        container.scrollLeft = target;
        if (container.scrollTo) {
          container.scrollTo({ left: target, behavior: "smooth" });
        }
      });
    },
  },
  beforeDestroy() {
    this.teardownThumbObserver();
    if (this.boundKeydown) {
      window.removeEventListener("keydown", this.boundKeydown, true);
      this.boundKeydown = null;
    }
  }
};
</script>

<style lang="scss">
.image-viewer-modal {
  .modal-dialog {
    max-width: 98vw;
    height: 95vh;
    margin: 2vh auto;
  }
  
  .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .modal-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 20px;
  }
  
  .image-viewer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .image-viewer-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 200px;
  }

  .empty-state {
    text-align: center;
    color: #6c757d;
  }

  .empty-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #495057;
  }

  .empty-subtitle {
    margin-top: 6px;
    font-size: 1rem;
  }
  
  .main-image-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 0;
    margin-bottom: 20px;
  }
  
  .main-image {
    width: 90vw;
    height: 60vh;
    object-fit: contain;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
  
  .image-caption {
    font-size: 1.1rem;
    color: #555;
    margin-top: 15px;
    text-align: center;
  }
  
  .image-thumbnails-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    min-height: 80px;
    background-color: #f8f9fa;
    padding: 10px;
    border-radius: 8px;
    overflow-x: auto;
    max-width: 100%;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }
  
  .thumbnail-mini {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    border: 2px solid #eee;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .thumbnail-mini:hover {
    transform: scale(1.05);
    border: 2px solid #f2671c;
  }
  
  .thumbnail-mini.active {
    border: 3px solid #f2671c;
    box-shadow: 0 0 8px #f2671c;
    transform: scale(1.1);
  }
}
</style>
